import { memo, useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { useBookings } from '../../hooks/useBookings'
import { vendorAPI } from '../../services/api/vendor'
import { createLogger } from '../../utils/logger'
import { toast } from '../../services/toastService'
import {
  trackBookingFunnelOpen, trackBookingStepView, trackBookingOfferingSelected,
  trackBookingStepAdvance, trackBookingStepBack, trackBookingSchedulingType,
  trackBookingSubmitAttempt, trackBookingSubmitSuccess, trackBookingSubmitError,
  trackBookingAbandoned,
} from '../../utils/analytics/bookingEvents'
import BookingStepOfferings from './BookingStepOfferings'
import BookingStepSchedule from './BookingStepSchedule'
import BookingStepConfirm from './BookingStepConfirm'

const logger = createLogger('BookingInlinePanel')

const STEP_OFFERINGS = 1
const STEP_SCHEDULE = 2
const STEP_CONFIRM = 3

/**
 * BookingInlinePanel - Same 3-step booking flow as BookingRequestModal but renders
 * inline inside the entity detail panel instead of as an overlay.
 * Used on mobile where fixed overlays get covered by the bottom sheet.
 * @param {string}   entityId
 * @param {string}   entityName
 * @param {function} onClose  - called when user exits booking flow
 * @param {function} [onSuccess]
 * @idempotent @atomic
 */
const BookingInlinePanel = memo(function BookingInlinePanel({ entityId, entityName, onClose, onSuccess, entityContact = null }) {
  const { t } = useTranslation()
  const { loading } = useAsyncOperation()
  const { createBooking } = useBookings()
  const SRC = 'inline_panel'
  const didSubmitRef = useRef(false)

  const [step, setStep] = useState(STEP_OFFERINGS)
  const [offerings, setOfferings] = useState(null)
  const [offeringsLoading, setOfferingsLoading] = useState(false)
  const [schedule, setSchedule] = useState({ schedules: [], upcoming_events: [] })
  const [selectedOffering, setSelectedOffering] = useState(null)
  const [schedulingType, setSchedulingType] = useState('single')
  const [scheduledDate, setScheduledDate] = useState('')
  const [scheduledTime, setScheduledTime] = useState('')
  const [contactHint, setContactHint] = useState('')
  const [notes, setNotes] = useState('')
  const [timePrefs, setTimePrefs] = useState([{ preferred_date: '', preferred_time: '' }])

  useEffect(() => {
    trackBookingFunnelOpen(entityId, entityName, SRC)
  }, [entityId, entityName])

  useEffect(() => {
    const names = ['offerings', 'schedule', 'confirm']
    trackBookingStepView(step, names[step - 1], entityId, SRC)
  }, [step, entityId])

  useEffect(() => {
    if (!entityId) return
    setOfferingsLoading(true)
    vendorAPI
      .getOfficialOfferings(entityId)
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : (res?.data?.offerings || [])
        setOfferings(list)
      })
      .catch((err) => { logger.error('Failed to load offerings', err); setOfferings([]) })
      .finally(() => setOfferingsLoading(false))
    vendorAPI.getEntitySchedule(entityId)
      .then((res) => setSchedule({ schedules: res?.data?.schedules || [], upcoming_events: res?.data?.upcoming_events || [] }))
      .catch(() => {})
  }, [entityId])

  const handleSelectOffering = useCallback((offering) => {
    setSelectedOffering(offering)
    if (offering) trackBookingOfferingSelected(entityId, offering.id, offering.name, offering.price, SRC)
  }, [entityId])

  const handleTypeChange = useCallback((type) => {
    setSchedulingType(type)
    trackBookingSchedulingType(type, entityId, SRC)
  }, [entityId])

  const addTimePref = useCallback(() => {
    if (timePrefs.length < 3) setTimePrefs((p) => [...p, { preferred_date: '', preferred_time: '' }])
  }, [timePrefs.length])

  const removeTimePref = useCallback((idx) => {
    setTimePrefs((p) => p.filter((_, i) => i !== idx))
  }, [])

  const updateTimePref = useCallback((idx, field, value) => {
    setTimePrefs((p) => p.map((item, i) => i === idx ? { ...item, [field]: value } : item))
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!selectedOffering) return
    trackBookingSubmitAttempt(entityId, selectedOffering.id, schedulingType, SRC)
    const item = { offering_id: selectedOffering.id, scheduling_type: schedulingType, notes }
    if (contactHint.trim()) item.address = contactHint.trim()
    if (schedulingType === 'single') {
      item.scheduled_date = scheduledDate
      item.scheduled_time = scheduledTime
    } else {
      item.time_preferences = timePrefs
        .filter((p) => p.preferred_date)
        .map((p, i) => ({ preference_order: i + 1, ...p }))
    }
    await createBooking(
      { entity_id: entityId, items: [item] },
      {
        onSuccess: (data) => {
          didSubmitRef.current = true
          toast.success(t('bookings.requestSent', 'Booking request sent!'))
          trackBookingSubmitSuccess(entityId, selectedOffering.id, schedulingType, SRC)
          onSuccess?.(data)
          onClose()
        },
        onError: () => {
          toast.error(t('bookings.requestFailed', 'Failed to send booking request'))
          trackBookingSubmitError(entityId, selectedOffering.id, SRC)
        },
      }
    )
  }, [selectedOffering, schedulingType, scheduledDate, scheduledTime, contactHint, notes, timePrefs, entityId, createBooking, t, onClose, onSuccess])

  const stepLabels = [
    t('bookings.selectService', 'Service'),
    t('bookings.scheduleStep', 'Schedule'),
    t('bookings.confirmStep', 'Confirm'),
  ]

  const canAdvance = step !== STEP_OFFERINGS || !!selectedOffering

  const handleClose = useCallback(() => {
    if (!didSubmitRef.current) trackBookingAbandoned(entityId, step, SRC)
    onClose()
  }, [entityId, step, onClose])

  const handleBack = useCallback(() => {
    if (step > STEP_OFFERINGS) {
      trackBookingStepBack(step, step - 1, entityId, SRC)
      setStep(step - 1)
    } else {
      handleClose()
    }
  }, [step, entityId, handleClose])

  const handleNext = useCallback(() => {
    if (!canAdvance) return
    trackBookingStepAdvance(step, step + 1, entityId, SRC)
    setStep(step + 1)
  }, [step, canAdvance, entityId])

  return (
    <div className="flex flex-col min-h-0 h-full">
      {/* Sticky header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 flex-shrink-0 bg-white sticky top-0 z-10">
        <button
          onClick={handleClose}
          onTouchEnd={(e) => { e.preventDefault(); handleClose() }}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
          aria-label={t('common.back', 'Back')}
        >
          <ArrowLeftIcon className="w-4 h-4 text-gray-600" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-1.5 min-w-0">
            <span className="text-sm font-semibold text-gray-900">{t('bookings.request', 'Book a Service')}</span>
            <span className="text-xs text-gray-400 truncate">&middot; {entityName}</span>
          </div>
          {/* Inline step progress */}
          <div className="flex items-center gap-0.5 mt-1">
            {stepLabels.map((label, i) => (
              <div key={i} className="flex items-center gap-0.5">
                <div className={`h-0.5 rounded-full transition-all ${
                  i + 1 < step ? 'w-6 bg-primary' : i + 1 === step ? 'w-8 bg-primary' : 'w-6 bg-gray-200'
                }`} />
                <span className={`text-[10px] font-medium whitespace-nowrap px-0.5 ${
                  i + 1 === step ? 'text-primary' : i + 1 < step ? 'text-primary/60' : 'text-gray-300'
                }`}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scrollable step content */}
      <div className="flex-1 overflow-y-auto overscroll-contain">
        {step === STEP_OFFERINGS && (
          <BookingStepOfferings
            offerings={offerings}
            offeringsLoading={offeringsLoading}
            selectedOffering={selectedOffering}
              onSelect={handleSelectOffering}
          />
        )}
        {step === STEP_SCHEDULE && (
          <BookingStepSchedule
            schedulingType={schedulingType}
              onTypeChange={handleTypeChange}
            scheduledDate={scheduledDate}
            onDateChange={setScheduledDate}
            scheduledTime={scheduledTime}
            onTimeChange={setScheduledTime}
            contactHint={contactHint}
            onContactHintChange={setContactHint}
            timePrefs={timePrefs}
            onAddTimePref={addTimePref}
            onRemoveTimePref={removeTimePref}
            onUpdateTimePref={updateTimePref}
            vendorSchedule={schedule.schedules}
            vendorEvents={schedule.upcoming_events}
            selectedOffering={selectedOffering}
          />
        )}
        {step === STEP_CONFIRM && (
          <BookingStepConfirm
            selectedOffering={selectedOffering}
            schedulingType={schedulingType}
            scheduledDate={scheduledDate}
            scheduledTime={scheduledTime}
            contactHint={contactHint}
            timePrefs={timePrefs}
            notes={notes}
            onNotesChange={setNotes}
            entityContact={entityContact}
          />
        )}
      </div>

      {/* Sticky footer */}
      <div className="flex gap-2 px-4 py-3 border-t border-gray-100 flex-shrink-0 bg-white sticky bottom-0">
        <button
          onClick={handleBack}
          onTouchEnd={(e) => { e.preventDefault(); handleBack() }}
          className="flex-1 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
          {step > STEP_OFFERINGS ? t('common.back', 'Back') : t('common.cancel', 'Cancel')}
        </button>
        {step < STEP_CONFIRM ? (
          <button
            disabled={!canAdvance}
            onClick={handleNext}
            onTouchEnd={(e) => { e.preventDefault(); handleNext() }}
            className="flex-1 py-2 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-40 hover:bg-primary/90 transition-colors">
            {t('common.next', 'Next')}
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            onTouchEnd={(e) => { e.preventDefault(); handleSubmit() }}
            disabled={loading}
            className="flex-1 py-2 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-40 hover:bg-primary/90 transition-colors"
          >
            {loading ? t('common.sending', 'Sending...') : t('bookings.sendRequest', 'Send Request')}
          </button>
        )}
      </div>
    </div>
  )
})

export default BookingInlinePanel
